import { type MailRibbonControlId, type MailRibbonTabId } from 'owa-ribbon-ids/lib/mailRibbonId';
import { constructKeytip, createAppToggleButton } from 'owa-command-ribbon';
import {
    createMailAppButton,
    createMailAppFlyoutAnchor,
    createMailAppSplitButton,
} from 'owa-mail-ribbon-utils/lib/createMailRibbonButton';
import {
    getDefaultSlrAppRibbonButtonStyles,
    getDefaultSlrSplitButtonAppRibbonButtonStyles,
} from 'owa-command-ribbon-styles';
import { onChangeNoteColorMenuClicked } from '../utils/deferredOnChangeNoteColorMenuClicked';

import { onViewGroupsMenuClicked } from '../utils/deferredOnViewGroupsMenuClicked';
import { onMyDayMenuClicked } from '../utils/deferredOnMyDayMenuClicked';
import {
    onDeleteNoteClicked,
    onRestoreClicked,
    onRestoreAllClicked,
    onRibbonCustomizerClicked,
    onUndoClicked,
    onViewEmailFromNoteClicked,
    onViewSettingsClicked,
    onRemindersClicked,
    onExpandCollapseConversationClicked,
    onSyncMailboxClicked,
    onLightRPClicked,
} from 'owa-mail-ribbon-command-actions/lib/commandActions';
import {
    openDiscoverGroups,
    openGroupsPanel,
} from 'owa-mail-ribbon-command-actions/lib/onGroupsCommandActions';
import type { ReadOnlyRibbonControlDefProps } from 'owa-mail-ribbon-utils';
import type { RibbonControlProps } from 'owa-acui';
import { owaComputedFn } from 'owa-computed-fn';
import { getComputedCallback } from 'owa-ribbon-mobx-utils/lib/getComputedCallback';
import { getControlInfo } from 'owa-mail-ribbon-utils/lib/getControlInfo';
import { getDefaultRibbonStyles } from 'owa-command-ribbon-styles/lib/util/getDefaultRibbonStyles';
import { getMenuDefinition } from '../../store/menuDefinitionStore';
import { onDensityMenuClicked } from '../utils/deferredOnDensityMenuClicked';
import { resetMenuDefinitionMutator } from '../../mutatorActions/menuDefinitionMutatorActions';
import { getGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';
import { getIsSearchTableShown } from 'owa-mail-list-store';
import { onFolderMenuOptionsMenuClicked } from '../utils/deferredOnFolderMenuOptionsMenuClicked';
import {
    getExitSearchAction,
    getSearchEntityAction,
    getSearchRefinerAction,
    isRefinerApplied,
    isSearchEntityFilterSelected,
} from '../../util/getSearchRibbonActions';
import { getRibbonMailboxInfo } from 'owa-mail-ribbon-utils/lib/getRibbonMailboxInfo';

/**
 * This file is for shared components in read-only scenarios. Meaning, any tab or control that only shows up
 * when a user is composing. In this case, Home tab, View tab, and Notes tab are good examples.
 *
 * For controls that show up in compose-only scenarios, please use composeSharedControlDefinitions.ts.
 */

export const getDeleteNoteControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps =
    owaComputedFn(function getDeleteNoteControl(
        props: ReadOnlyRibbonControlDefProps
    ): RibbonControlProps {
        const controlInfo = getControlInfo(640, props);
        return createMailAppButton(
            640,
            controlInfo.text,
            controlInfo.tooltip,
            props,
            getComputedCallback(640, onDeleteNoteClicked),
            constructKeytip([6], 640),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultSlrSplitButtonAppRibbonButtonStyles()
        );
    });

export const getChangeNoteColorControl: (
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getChangeNoteColorControl(
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getControlInfo(641, props);
    const storeName = 'ChangeNoteColorMenuDefinitionStore';
    return createMailAppFlyoutAnchor(
        641,
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getMenuDefinition(storeName),
        constructKeytip([6], 641, true /* hasMenu */),
        controlInfo.iconName,
        undefined /*iconColor*/,
        getDefaultRibbonStyles(),
        undefined /* itemId */,
        {
            onExecute: () => {
                onChangeNoteColorMenuClicked(props);
            },
            onAfterMenuDismiss: () => {
                resetMenuDefinitionMutator(storeName);
            },
        }
    );
});

export const getNotesViewEmailControl: (
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getNotesViewEmailControl(
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getControlInfo(642, props);
    return createMailAppButton(
        642,
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getComputedCallback(642, onViewEmailFromNoteClicked),
        constructKeytip([6], 642),
        controlInfo.iconName,
        undefined /*iconColor*/,
        getDefaultRibbonStyles()
    );
});

/**
 * Gets the Has Attachment refiner control definition
 */
export const getHasAttachmentRefinerControl: (
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getHasAttachmentRefinerControl(
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getControlInfo(741, props);

    return createAppToggleButton(
        741,
        controlInfo.text,
        getComputedCallback(741, () => getSearchRefinerAction('HasAttachment')),
        isRefinerApplied('HasAttachment'),
        constructKeytip([14], 741),
        controlInfo.iconName,
        undefined /*iconColor*/,
        getDefaultRibbonStyles()
    );
});

/**
 * Gets the Is Unread refiner control definition
 */
export const getIsUnreadRefinerControl: (
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getIsUnreadRefinerControl(
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getControlInfo(742, props);

    return createAppToggleButton(
        742,
        controlInfo.text,
        getComputedCallback(742, () => getSearchRefinerAction('IsUnread')),
        isRefinerApplied('IsUnread'),
        constructKeytip([14], 742),
        controlInfo.iconName,
        undefined /*iconColor*/,
        getDefaultRibbonStyles()
    );
});

/**
 * Gets the Is Mentioned refiner control definition
 */
export const getIsMentionedRefinerControl: (
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getIsMentionedRefinerControl(
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getControlInfo(743, props);

    return createAppToggleButton(
        743,
        controlInfo.text,
        getComputedCallback(743, () => getSearchRefinerAction('IsMentioned')),
        isRefinerApplied('IsMentioned'),
        constructKeytip([14], 743),
        controlInfo.iconName,
        undefined /*iconColor*/,
        getDefaultRibbonStyles()
    );
});

/**
 * Gets the Is Mentioned In Chats refiner control definition
 */
export const getIsMentionedInChatsRefinerControl: (
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getIsMentionedInChatsRefinerControl(
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getControlInfo(744, props);

    return createAppToggleButton(
        744,
        controlInfo.text,
        getComputedCallback(744, () => getSearchRefinerAction('IsMentionedInChats')),
        isRefinerApplied('IsMentionedInChats'),
        constructKeytip([14], 744),
        controlInfo.iconName,
        undefined /*iconColor*/,
        getDefaultRibbonStyles()
    );
});

/**
 * Gets the Is Flagged refiner control definition
 */
export const getIsFlaggedRefinerControl: (
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getIsFlaggedRefinerControl(
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getControlInfo(745, props);

    return createAppToggleButton(
        745,
        controlInfo.text,
        getComputedCallback(745, () => getSearchRefinerAction('IsFlagged')),
        isRefinerApplied('IsFlagged'),
        constructKeytip([14], 745),
        controlInfo.iconName,
        undefined /*iconColor*/,
        getDefaultRibbonStyles()
    );
});

/**
 * Gets the High Importance refiner control definition
 */
export const getHighImportanceRefinerControl: (
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getHighImportanceRefinerControl(
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getControlInfo(746, props);

    return createAppToggleButton(
        746,
        controlInfo.text,
        getComputedCallback(746, () => getSearchRefinerAction('HighImportance')),
        isRefinerApplied('HighImportance'),
        constructKeytip([14], 746),
        controlInfo.iconName,
        undefined /*iconColor*/,
        getDefaultRibbonStyles()
    );
});

/**
 * Gets the To Me refiner control definition
 */
export const getToMeRefinerControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps =
    owaComputedFn(function getToMeRefinerControl(
        props: ReadOnlyRibbonControlDefProps
    ): RibbonControlProps {
        const controlInfo = getControlInfo(747, props);

        return createAppToggleButton(
            747,
            controlInfo.text,
            getComputedCallback(747, () => getSearchRefinerAction('ToMe')),
            isRefinerApplied('ToMe'),
            constructKeytip([14], 747),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultRibbonStyles()
        );
    });

/**
 * Gets the Date Range refiner control definition
 */
export const getDateRangeRefinerControl: (
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getDateRangeRefinerControl(
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getControlInfo(748, props);
    return createMailAppButton(
        748,
        controlInfo.text,
        controlInfo.tooltip,
        props,
        () => {
            // TODO: Implement date range refiner action
        },
        constructKeytip([14], 748),
        controlInfo.iconName,
        undefined /*iconColor*/,
        getDefaultRibbonStyles(),
        undefined /*itemId*/
    );
});

/**
 * Gets the Advanced Search control definition
 */
export const getAdvancedSearchControl: (
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getAdvancedSearchControl(
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getControlInfo(739, props);
    return createMailAppButton(
        739,
        controlInfo.text,
        controlInfo.tooltip,
        props,
        () => {
            // TODO: Implement advanced search action
        },
        constructKeytip([14], 739),
        controlInfo.iconName,
        undefined /*iconColor*/,
        getDefaultRibbonStyles(),
        undefined /*itemId*/
    );
});

/**
 * Gets the Clear Search control definition
 */
export const getCloseSearchControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps =
    owaComputedFn(function getCloseSearchControl(
        props: ReadOnlyRibbonControlDefProps
    ): RibbonControlProps {
        const controlInfo = getControlInfo(740, props);
        return createMailAppButton(
            740,
            controlInfo.text,
            controlInfo.tooltip,
            props,
            getComputedCallback(740, () => getExitSearchAction()),
            constructKeytip([14], 740),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultRibbonStyles(),
            undefined /*itemId*/
        );
    });

/**
 * Gets the Include Channels refiner control definition
 */
export const getIncludeChannelsRefinerControl: (
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getIncludeChannelsRefinerControl(
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getControlInfo(749, props);

    return createAppToggleButton(
        749,
        controlInfo.text,
        getComputedCallback(749, () => getSearchRefinerAction('IncludeChannels')),
        isRefinerApplied('IncludeChannels'),
        constructKeytip([14], 749),
        controlInfo.iconName,
        undefined /*iconColor*/,
        getDefaultRibbonStyles()
    );
});

/**
 * Gets the Entity All control definition
 */
export const getEntityAllControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps =
    owaComputedFn(function getEntityAllControl(
        props: ReadOnlyRibbonControlDefProps
    ): RibbonControlProps {
        const controlInfo = getControlInfo(754, props);

        return createAppToggleButton(
            754,
            controlInfo.text,
            async () => {
                getSearchEntityAction('All');
            },
            isSearchEntityFilterSelected('All'),
            constructKeytip([14], 754),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultRibbonStyles()
        );
    });

/**
 * Gets the Entity Mail control definition
 */
export const getEntityMailControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps =
    owaComputedFn(function getEntityMailControl(
        props: ReadOnlyRibbonControlDefProps
    ): RibbonControlProps {
        const controlInfo = getControlInfo(750, props);

        return createAppToggleButton(
            750,
            controlInfo.text,
            async () => {
                getSearchEntityAction('Mail');
            },
            isSearchEntityFilterSelected('Mail'),
            constructKeytip([14], 750),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultRibbonStyles()
        );
    });

/**
 * Gets the Entity Chats control definition
 */
export const getEntityChatsControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps =
    owaComputedFn(function getEntityChatsControl(
        props: ReadOnlyRibbonControlDefProps
    ): RibbonControlProps {
        const controlInfo = getControlInfo(751, props);

        return createAppToggleButton(
            751,
            controlInfo.text,
            async () => {
                getSearchEntityAction('Chats');
            },
            isSearchEntityFilterSelected('Chats'),
            constructKeytip([14], 751),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultRibbonStyles()
        );
    });

/**
 * Gets the Entity Files control definition
 */
export const getEntityFilesControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps =
    owaComputedFn(function getEntityFilesControl(
        props: ReadOnlyRibbonControlDefProps
    ): RibbonControlProps {
        const controlInfo = getControlInfo(752, props);

        return createAppToggleButton(
            752,
            controlInfo.text,
            async () => {
                getSearchEntityAction('Files');
            },
            isSearchEntityFilterSelected('Files'),
            constructKeytip([14], 752),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultRibbonStyles()
        );
    });

/**
 * Gets the Entity People control definition
 */
export const getEntityPeopleControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps =
    owaComputedFn(function getEntityPeopleControl(
        props: ReadOnlyRibbonControlDefProps
    ): RibbonControlProps {
        const controlInfo = getControlInfo(753, props);

        return createAppToggleButton(
            753,
            controlInfo.text,
            async () => {
                getSearchEntityAction('People');
            },
            isSearchEntityFilterSelected('People'),
            constructKeytip([14], 753),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultRibbonStyles()
        );
    });

export const getRibbonCustomizerControl: (
    tabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getRibbonCustomizerControl(
    tabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getControlInfo(558, props);
    const mailboxInfo = props.mailboxInfo ?? getGlobalSettingsAccountMailboxInfo();
    return createMailAppButton(
        558,
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getComputedCallback(558, onRibbonCustomizerClicked, tabId, mailboxInfo),
        constructKeytip([tabId], 558),
        undefined /* icon */,
        undefined /* iconColor */,
        getDefaultSlrAppRibbonButtonStyles()
    );
});

export const getDensityControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getDensityControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getControlInfo(649, props);
    const storeName = 'DensityMenuDefinitionStore';
    return createMailAppFlyoutAnchor(
        649,
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getMenuDefinition(storeName),
        constructKeytip([keytipTabId], 649, true /* hasMenu */),
        controlInfo.iconName,
        undefined /* iconColor */,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
        undefined /* itemId */,
        {
            onExecute: () => {
                onDensityMenuClicked();
            },
            onAfterMenuDismiss: () => {
                resetMenuDefinitionMutator(storeName);
            },
        }
    );
});

export const getViewSettingsControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getViewSettingsControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getControlInfo(643, props);
    return createMailAppButton(
        643,
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getComputedCallback(643, onViewSettingsClicked, props),
        constructKeytip([keytipTabId], 643),
        controlInfo.iconName,
        undefined /* iconColor */,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined
    );
});

export const getDiscoverGroupsControl: (
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getDiscoverGroupsControl(
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getControlInfo(508, props);
    return createMailAppButton(
        508,
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getComputedCallback(508, openDiscoverGroups, props),
        constructKeytip([keytipTabId], 508),
        controlInfo.iconName
    );
});

export const getViewGroupsPanelControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getViewGroupsPanelControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getControlInfo(663, props);
    const storeName = 'ViewGroupsMenuDefinitionStore';

    return createMailAppSplitButton(
        663,
        controlInfo.text,
        controlInfo.ariaText,
        controlInfo.tooltip,
        props,
        getMenuDefinition(storeName),
        getComputedCallback(663, openGroupsPanel),
        constructKeytip([keytipTabId], 663, true /* hasMenu */),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrSplitButtonAppRibbonButtonStyles() : undefined,
        undefined /*itemId*/,
        {
            onMenuButtonExecute: () => {
                onViewGroupsMenuClicked(keytipTabId, props);
            },
            onAfterMenuDismiss: () => {
                resetMenuDefinitionMutator(storeName);
            },
        }
    );
});

export const getUndoControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getUndoControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getControlInfo(580, props);
    return createMailAppButton(
        580,
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getComputedCallback(580, onUndoClicked),
        constructKeytip([keytipTabId], 580),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined
    );
});

export const getRestoreControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getRestoreControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getControlInfo(683, props);
    return createMailAppButton(
        683,
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getComputedCallback(683, onRestoreClicked),
        constructKeytip([keytipTabId], 683),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined
    );
});

export const getRestoreAllControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getRestoreAllControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getControlInfo(684, props);
    return createMailAppButton(
        684,
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getComputedCallback(684, onRestoreAllClicked),
        constructKeytip([keytipTabId], 684),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined
    );
});

export const getRemindersWindowControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getRemindesWindowControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getControlInfo(690, props);
    return createMailAppButton(
        690,
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getComputedCallback(690, onRemindersClicked, mailboxInfo),
        constructKeytip([keytipTabId], 690),
        controlInfo.iconName,
        undefined /* iconColor */,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
        undefined /* itemId */
    );
});
export const getExpandCollapseConversationControl: (
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getExpandCollapseConversationControl(
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getControlInfo(687, props);
    return createMailAppButton(
        687,
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getComputedCallback(687, onExpandCollapseConversationClicked, props.itemId?.Id ?? ''),
        constructKeytip([3], 687, true /* hasMenu */),
        controlInfo.iconName,
        undefined /*iconColor*/,
        getDefaultRibbonStyles()
    );
});

export const getLightRPControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps =
    owaComputedFn(function getLightRPControl(
        props: ReadOnlyRibbonControlDefProps
    ): RibbonControlProps {
        const controlInfo = getControlInfo(720, props);
        return createMailAppButton(
            720,
            controlInfo.text,
            controlInfo.tooltip,
            props,
            getComputedCallback(720, onLightRPClicked),
            constructKeytip([3], 720),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultRibbonStyles()
        );
    });

export const getSyncMailboxControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getSyncMailboxControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getControlInfo(701, props);
    return createMailAppButton(
        701,
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getComputedCallback(701, onSyncMailboxClicked),
        constructKeytip([keytipTabId], 701),
        controlInfo.iconName,
        undefined,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
        undefined /*itemId*/,
        {
            disabled: !props.mailboxInfo || getIsSearchTableShown(),
        }
    );
});

export const getMyDayControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps =
    owaComputedFn(function getMyDayControl(
        props: ReadOnlyRibbonControlDefProps
    ): RibbonControlProps {
        const controlInfo = getControlInfo(706, props);
        const storeName = 'MyDayOptionsMenuDefinitionStore';
        return createMailAppFlyoutAnchor(
            706,
            controlInfo.text,
            controlInfo.tooltip,
            props,
            getMenuDefinition(storeName),
            constructKeytip([3], 706, true /* hasMenu */),
            controlInfo.iconName,
            undefined /* iconColor */,
            undefined /* styles */,
            undefined /* itemId */,
            {
                onExecute: () => {
                    onMyDayMenuClicked();
                },
                onAfterMenuDismiss: () => {
                    resetMenuDefinitionMutator(storeName);
                },
            }
        );
    });

export const getFolderPaneControl: (
    isSLR: boolean,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getFolderPaneControl(
    isSLR: boolean,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getControlInfo(647, props);
    const storeName = 'FolderMenuOptionsMenuDefinitionStore';
    return createMailAppFlyoutAnchor(
        647,
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getMenuDefinition(storeName),
        constructKeytip([3], 647, true /* hasMenu */),
        controlInfo.iconName,
        undefined /* iconColor */,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
        undefined /* itemId */,
        {
            onExecute: () => {
                onFolderMenuOptionsMenuClicked(props);
            },
            onAfterMenuDismiss: () => {
                resetMenuDefinitionMutator(storeName);
            },
        }
    );
});
